// /login — email + password (00 §4). Error states (per legacy + 00 §3.2/§4):
//   412 / EMAIL_NOT_VERIFIED → offer resend-verify
//   426 / UPGRADE_REQUIRED   → forced-upgrade blocking notice (client below force_upgrade_below)
//   anything else            → inline message
// On success the httpOnly refresh cookie is set by the backend; the access token
// stays in memory (AuthContext).

import { brandDisplayName, brandLogoSrc } from '../../lib/brand';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError, resendVerify } from '../../lib/api';
import { PasswordInput } from '../../components/PasswordInput';

export default function LoginPage() {
  const { t } = useTranslation();
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // 雙主軸 landing（apps/home/specs/ia.md §0）：登入後落「總覽」(/home)，事件↔裝置對等。
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/home';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [resent, setResent] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNeedsVerify(false);
    setResent(false);
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 412 || err.code === 'EMAIL_NOT_VERIFIED')) {
        setError(t('login.needVerify'));
        setNeedsVerify(true);
      } else if (err instanceof ApiError && (err.status === 426 || err.code === 'UPGRADE_REQUIRED')) {
        setUpgradeRequired(true);
      } else if (err instanceof ApiError && (err.status === 403 || err.code === 'ACCOUNT_INACTIVE')) {
        // restful#121:deleting/banned/suspended 帳號 login → 403 ACCOUNT_INACTIVE(不發 token)。
        // 無法與封禁區分,顯示 spec 20 §5.4 通用阻斷文案(涵蓋刪除中→引導確認信/客服)。
        setError(t('login.accountInactive'));
      } else if (err instanceof ApiError) {
        setError(t('login.failGeneric', { msg: err.message }));
      } else {
        setError(t('login.failNetwork'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setBusy(true);
    setError(null);
    try {
      await resendVerify(email);
      setResent(true);
    } catch (err) {
      setError(err instanceof ApiError ? t('login.resendFail', { msg: err.message }) : t('login.resendFailNetwork'));
    } finally {
      setBusy(false);
    }
  }

  if (upgradeRequired) {
    // 00 §4: force_upgrade_below → 426 UPGRADE_REQUIRED; block sign-in entirely.
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
        <section className="card" style={{ maxWidth: 420, textAlign: 'center' }}>
          <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-4xl)' }}>{t('login.upgradeTitle')}</h1>
          <p className="text-secondary">{t('login.upgradeBody')}</p>
          <button className="btn-primary" onClick={() => window.location.reload()}>
            {t('common.refresh')}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
      <section className="card" style={{ width: 'min(400px, 100%)' }}>
        <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-5xl)', letterSpacing: 'var(--letter-spacing-tight)', display: 'flex', alignItems: 'center', gap: 10 }}><img src={brandLogoSrc()} alt="" width={36} height={36} style={{ borderRadius: 8, objectFit: 'contain' }} />{brandDisplayName()}</h1>
        <p className="text-secondary" style={{ marginTop: 'calc(-1 * var(--spacing-4))' }}>
          {t('login.subtitle')}
        </p>
        <form onSubmit={onSubmit} style={{ display: 'grid', gap: 'var(--spacing-6)' }}>
          <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
              {t('login.email')}
            </span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={error !== null}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </label>
          <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
              {t('login.password')}
            </span>
            <PasswordInput
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={error !== null}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </label>
          {error && (
            <p id="login-error" className="error-text" role="alert" style={{ margin: 0 }}>
              {error}
            </p>
          )}
          {resent && (
            <p style={{ margin: 0, color: 'var(--color-semantic-success)', fontSize: 'var(--font-size-md)' }} role="status">
              {t('login.resent')}
            </p>
          )}
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('login.submitBusy') : t('login.submit')}
          </button>
          {needsVerify && (
            <button type="button" className="btn-ghost" disabled={busy || !email} onClick={() => void onResend()}>
              {t('login.resend')}
            </button>
          )}
        </form>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 'var(--spacing-4)',
            marginTop: 'var(--spacing-6)',
            fontSize: 'var(--font-size-md)',
            flexWrap: 'wrap',
          }}
        >
          <Link to="/register">{t('login.createAccount')}</Link>
          <Link to="/forgot-password">{t('login.forgotPassword')}</Link>
        </div>
      </section>
    </div>
  );
}
